import { useCallback, useEffect, useMemo, useState } from "react";
import { Drawer, Input, Select, message } from "antd";
import { useSearchParams } from "react-router-dom";
import styled from "styled-components";
import { adminPaymentsAPI, type AdminPaymentRow, type AdminPaymentsFilters } from "@/api/adminPayments";
import { agencyFinanceAPI } from "@/api/agencyFinance";
import { paymentStatementAPI, type PaymentStatement } from "@/api/paymentStatement";
import { CampaignStyledTable } from "@/features/campaign";
import { PaymentOutcomeModal, type PaymentOutcome } from "@/features/finance";
import { PaymentStatementView } from "@/features/payment-statement";
import { AppButton, Icon } from "@/shared/components";
import type { CustomTableColumn } from "@/shared/components/Table";
import { getApiErrorMessage } from "@/shared/utils/apiError";
import { phoneCountries } from "@/shared/utils/phoneCountries";
import {
	PAYMENT_STATUS_OPTIONS,
	STATEMENT_STATUS_OPTIONS,
	parsePaymentsSearch,
	toPaymentsSearch,
} from "../utils/paymentsFilters";
import { useColumnVisibility } from "../hooks/useColumnVisibility";
import { toColumnSpecs } from "../utils/columnVisibility";
import { ColumnSettings, ScrollTable } from "./ColumnSettings";
import { CountryLabel } from "./CountryLabel";

const PAGE_SIZE = 25;
/** Off by default; the gear turns them on and the choice is remembered in this browser. */
const PAYMENTS_DEFAULT_HIDDEN = ["principal", "tax"];
const money = (value: number | null, currency: string) =>
	value === null
		? "—"
		: `${value.toLocaleString("en-AU", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ${currency}`;
const date = (iso: string | null) =>
	iso
		? new Intl.DateTimeFormat("en-AU", { day: "numeric", month: "short", year: "numeric" }).format(new Date(iso))
		: "—";
const TRANSITION_DONE = {
	in_process: "Scheduled for payment.",
	completed: "Marked as paid.",
	on_hold: "Payment on hold.",
	rejected_by_finance: "Payment rejected.",
} as const;
const STATUS_LABEL: Record<AdminPaymentRow["paymentStatus"], string> = {
	not_started: "Not started",
	submitted: "Submitted",
	in_review: "In review",
	in_process: "In process",
	on_hold: "On hold",
	completed: "Paid",
	failed: "Failed",
	rejected_by_finance: "Rejected",
};

/**
 * Admin › Payments (Ticket-348 §5.8): every Payment Statement across campaigns, filtered and paged
 * server-side, with the filters in the URL. Finance acts on the instruction; the statement is read-only.
 */
export const FinancePaymentsTab = () => {
	const [searchParams, setSearchParams] = useSearchParams();
	const { filters, page } = useMemo(() => parsePaymentsSearch(searchParams), [searchParams]);
	const [draftQuery, setDraftQuery] = useState(filters.q ?? "");
	const [data, setData] = useState<{ items: AdminPaymentRow[]; total: number }>({ items: [], total: 0 });
	const [loading, setLoading] = useState(true);
	const [exporting, setExporting] = useState(false);
	const [selected, setSelected] = useState<AdminPaymentRow | null>(null);
	const [statement, setStatement] = useState<PaymentStatement | null>(null);
	const [outcomeOpen, setOutcomeOpen] = useState(false);
	const [updating, setUpdating] = useState(false);

	const apply = useCallback(
		(next: AdminPaymentsFilters, nextPage = 1) =>
			setSearchParams(toPaymentsSearch(next, nextPage), { replace: true }),
		[setSearchParams],
	);

	const load = useCallback(async () => {
		setLoading(true);
		try {
			const result = await adminPaymentsAPI.list(filters, { limit: PAGE_SIZE, offset: (page - 1) * PAGE_SIZE });
			setData({ items: result.items, total: result.total });
		} catch (error) {
			message.error(getApiErrorMessage(error, "Unable to load payments"));
		} finally {
			setLoading(false);
		}
	}, [filters, page]);

	useEffect(() => {
		void load();
	}, [load]);

	useEffect(() => {
		setDraftQuery(filters.q ?? "");
	}, [filters.q]);

	useEffect(() => {
		if (!selected) {
			setStatement(null);
			return;
		}
		let cancelled = false;
		setStatement(null);
		paymentStatementAPI
			.getForFinancePayment(selected.paymentInstructionId)
			.then((row) => {
				if (!cancelled) setStatement(row);
			})
			.catch((error) => {
				if (!cancelled) message.error(getApiErrorMessage(error, "Unable to load the Payment Statement"));
			});
		return () => {
			cancelled = true;
		};
	}, [selected]);

	const exportCsv = async () => {
		setExporting(true);
		try {
			await adminPaymentsAPI.exportCsv(filters);
		} catch (error) {
			message.error(getApiErrorMessage(error, "Unable to export"));
		} finally {
			setExporting(false);
		}
	};

	const transition = async (outcome: PaymentOutcome) => {
		if (!selected) return;
		const { status, ...extra } = outcome;
		setUpdating(true);
		try {
			const updated = await agencyFinanceAPI.updateStatus(selected.paymentInstructionId, status, extra);
			const next: AdminPaymentRow = {
				...selected,
				paymentStatus: updated.paymentStatus,
				paymentStatusReason: updated.paymentStatusReason,
				paidAt: updated.dateCompleted,
				transactionReference: updated.disbursementReference,
			};
			setSelected(next);
			setData((d) => ({ ...d, items: d.items.map((i) => (i.statementId === next.statementId ? next : i)) }));
			setOutcomeOpen(false);
			message.success(TRANSITION_DONE[status]);
		} catch (error) {
			message.error(getApiErrorMessage(error, "Unable to update the payment"));
		} finally {
			setUpdating(false);
		}
	};

	const columns: CustomTableColumn<AdminPaymentRow>[] = [
		{ title: "Statement No.", key: "no", width: 190, render: (_, r) => <Strong>{r.statementNumber}</Strong> },
		{ title: "Issued", key: "issued", width: 120, render: (_, r) => date(r.issuedAt) },
		{ title: "Creator (legal name)", key: "creator", width: 220, render: (_, r) => r.creatorLegalName },
		{ title: "Country", key: "country", width: 160, render: (_, r) => <CountryLabel code={r.countryCode} /> },
		{ title: "Campaign", key: "campaign", width: 260, render: (_, r) => `${r.campaignName} · ${r.projectCode}` },
		{ title: "Principal", key: "principal", width: 140, render: (_, r) => money(r.principalAmount, r.currency) },
		{
			title: "Tax",
			key: "tax",
			width: 130,
			render: (_, r) => (r.taxRate === null ? "—" : `${r.taxName} ${money(r.declaredTaxAmount, r.currency)}`),
		},
		{
			title: "Payable",
			key: "payable",
			width: 140,
			render: (_, r) => <Strong>{money(r.amountPayable, r.currency)}</Strong>,
		},
		{ title: "Statement", key: "sstatus", width: 110, render: (_, r) => <Pill>{r.statementStatus}</Pill> },
		{
			title: "Payment",
			key: "pstatus",
			width: 110,
			render: (_, r) => <Pill $paid={r.paymentStatus === "completed"}>{STATUS_LABEL[r.paymentStatus]}</Pill>,
		},
		{ title: "Paid", key: "paid", width: 120, render: (_, r) => date(r.paidAt) },
		{ title: "Reference", key: "ref", width: 150, render: (_, r) => r.transactionReference ?? "—" },
	];
	const columnSettings = useColumnVisibility("payments", toColumnSpecs(columns), PAYMENTS_DEFAULT_HIDDEN);

	return (
		<>
			<Filters>
				<Search
					aria-label="Search statements"
					placeholder="Statement, creator, campaign, project code, reference"
					suffix={<Icon src="/icon/menu_search.svg" size={22} />}
					value={draftQuery}
					onChange={(e) => setDraftQuery(e.target.value)}
					onPressEnter={() => apply({ ...filters, q: draftQuery.trim() || undefined })}
					onBlur={() =>
						draftQuery.trim() !== (filters.q ?? "") &&
						apply({ ...filters, q: draftQuery.trim() || undefined })
					}
				/>
				<FilterSelect
					mode="multiple"
					allowClear
					placeholder="Payment status"
					value={filters.paymentStatus ?? []}
					options={PAYMENT_STATUS_OPTIONS as unknown as { value: string; label: string }[]}
					onChange={(value) =>
						apply({ ...filters, paymentStatus: value as AdminPaymentsFilters["paymentStatus"] })
					}
				/>
				<FilterSelect
					mode="multiple"
					allowClear
					placeholder="Statement status"
					value={filters.statementStatus ?? []}
					options={STATEMENT_STATUS_OPTIONS as unknown as { value: string; label: string }[]}
					onChange={(value) =>
						apply({ ...filters, statementStatus: value as AdminPaymentsFilters["statementStatus"] })
					}
				/>
				<FilterSelect
					mode="multiple"
					allowClear
					showSearch
					optionFilterProp="label"
					placeholder="Country"
					value={filters.country ?? []}
					options={phoneCountries.map((c) => ({ value: c.iso, label: c.name }))}
					onChange={(value) => apply({ ...filters, country: value as string[] })}
				/>
				<FilterSelect
					mode="tags"
					allowClear
					placeholder="Currency (e.g. AUD)"
					value={filters.currency ?? []}
					onChange={(value) =>
						apply({ ...filters, currency: (value as string[]).map((v) => v.toUpperCase()) })
					}
				/>
				<DateRange>
					<span>Issued</span>
					<DateInput
						type="date"
						aria-label="Issued from"
						value={filters.issuedFrom ?? ""}
						onChange={(e) => apply({ ...filters, issuedFrom: e.target.value || undefined })}
					/>
					<span>–</span>
					<DateInput
						type="date"
						aria-label="Issued to"
						value={filters.issuedTo ?? ""}
						onChange={(e) => apply({ ...filters, issuedTo: e.target.value || undefined })}
					/>
				</DateRange>
				<DateRange>
					<span>Paid</span>
					<DateInput
						type="date"
						aria-label="Paid from"
						value={filters.paidFrom ?? ""}
						onChange={(e) => apply({ ...filters, paidFrom: e.target.value || undefined })}
					/>
					<span>–</span>
					<DateInput
						type="date"
						aria-label="Paid to"
						value={filters.paidTo ?? ""}
						onChange={(e) => apply({ ...filters, paidTo: e.target.value || undefined })}
					/>
				</DateRange>
				<Spacer />
				<AppButton variant="outline" onClick={() => apply({})} disabled={Object.keys(filters).length === 0}>
					Clear
				</AppButton>
				<AppButton variant="primary" onClick={() => void exportCsv()} disabled={exporting || data.total === 0}>
					{exporting ? "Exporting…" : `Export CSV (${data.total})`}
				</AppButton>
				<ColumnSettings
					columns={toColumnSpecs(columns)}
					hidden={columnSettings.hidden}
					onToggle={columnSettings.toggle}
					onReset={columnSettings.reset}
				/>
			</Filters>
			<ScrollTable $minWidth={columnSettings.minWidth}>
				<CampaignStyledTable<AdminPaymentRow>
					rowKey="statementId"
					columns={columns.filter((column) => !columnSettings.hidden.includes(String(column.key)))}
					dataSource={data.items}
					loading={loading}
					size="middle"
					locale={{ emptyText: "No Payment Statements match these filters." }}
					pagination={{
						current: page,
						pageSize: PAGE_SIZE,
						total: data.total,
						showSizeChanger: false,
						onChange: (p) => apply(filters, p),
					}}
					onRow={(row) => ({ onClick: () => setSelected(row), style: { cursor: "pointer" } })}
				/>
			</ScrollTable>
			<Drawer
				open={Boolean(selected)}
				onClose={() => setSelected(null)}
				width={620}
				title={selected ? selected.statementNumber : ""}
				destroyOnClose
			>
				{selected ? (
					<DrawerBody>
						{statement ? (
							<PaymentStatementView statement={statement} audience="finance" />
						) : (
							<Muted>Loading the Payment Statement…</Muted>
						)}
						<Meta>
							<span>Payment status</span>
							<strong>{STATUS_LABEL[selected.paymentStatus]}</strong>
							<span>Paid</span>
							<strong>{date(selected.paidAt)}</strong>
							<span>Transaction reference</span>
							<strong>{selected.transactionReference ?? "—"}</strong>
							{selected.paymentStatus === "on_hold" ||
							selected.paymentStatus === "rejected_by_finance" ? (
								<>
									<span>
										{selected.paymentStatus === "on_hold" ? "Hold reason" : "Rejection reason"}
									</span>
									<strong>{selected.paymentStatusReason ?? "No reason recorded"}</strong>
								</>
							) : null}
						</Meta>
						<DrawerActions>
							{selected.paymentStatus !== "completed" ? (
								<AppButton variant="primary" disabled={updating} onClick={() => setOutcomeOpen(true)}>
									Acknowledge &amp; Process
								</AppButton>
							) : null}
						</DrawerActions>
					</DrawerBody>
				) : null}
			</Drawer>
			{selected ? (
				<PaymentOutcomeModal
					open={outcomeOpen}
					currentStatus={selected.paymentStatus}
					statementNumber={selected.statementNumber}
					payeeLabel={selected.creatorLegalName}
					campaignName={selected.campaignName}
					amountLabel={money(selected.amountPayable, selected.currency)}
					submitting={updating}
					onCancel={() => setOutcomeOpen(false)}
					onConfirm={(outcome) => void transition(outcome)}
				/>
			) : null}
		</>
	);
};

const Filters = styled.div`
	display: flex;
	flex-wrap: wrap;
	align-items: center;
	gap: 12px;
	margin-bottom: 16px;
`;
const Search = styled(Input)`
	width: 340px;
	&& {
		height: 40px;
		border-color: #cdd4f6;
		border-radius: 8px;
	}
`;
const FilterSelect = styled(Select)`
	min-width: 180px;
	.ant-select-selector {
		min-height: 40px !important;
		border-color: #cdd4f6 !important;
		border-radius: 8px !important;
	}
` as typeof Select;
const DateRange = styled.label`
	display: flex;
	align-items: center;
	gap: 6px;
	color: #707070;
	font-size: 13px;
`;
const DateInput = styled.input`
	height: 40px;
	padding: 0 10px;
	border: 1px solid #cdd4f6;
	border-radius: 8px;
	font: inherit;
	font-size: 13px;
`;
const Spacer = styled.span`
	flex: 1;
`;
const Strong = styled.span`
	font-weight: 600;
	color: #303030;
`;
const Muted = styled.p`
	color: #707070;
	font-size: 14px;
`;
const Pill = styled.span<{ $paid?: boolean }>`
	display: inline-flex;
	padding: 2px 8px;
	border-radius: 24px;
	font-size: 12px;
	font-weight: 500;
	text-transform: capitalize;
	background: ${({ $paid }) => ($paid ? "#bfef90" : "#dde2fa")};
	color: ${({ $paid }) => ($paid ? "#377608" : "#2a057f")};
`;
const DrawerBody = styled.div`
	display: flex;
	flex-direction: column;
	gap: 16px;
`;
const Meta = styled.dl`
	display: grid;
	grid-template-columns: max-content 1fr;
	gap: 8px 16px;
	margin: 0;
	padding: 12px 16px;
	border: 1px solid #cdd4f6;
	border-radius: 12px;
	font-size: 14px;
	span {
		color: #707070;
	}
	strong {
		color: #303030;
	}
`;
const DrawerActions = styled.div`
	display: flex;
	justify-content: flex-end;
	gap: 12px;
`;
